// src/Components/videochat/useConnectionQuality.js
//
// Calidad de la red de la llamada, igual que la app iOS: cada 2 s se leen las
// estadísticas del RTCPeerConnection y una muestra es "mala" con rtt > 0,45 s
// o pérdida > 8 %. Dos malas seguidas muestran «Conexión débil»; dos buenas
// seguidas lo quitan (histéresis, para que no parpadee por un pico suelto).
import { useEffect, useRef, useState } from 'react'

const SAMPLE_MS = 2000
const BAD_RTT_S = 0.45
const BAD_LOSS = 0.08
const SAMPLES_TO_FLIP = 2

export default function useConnectionQuality(pcRef, enabled) {
  const [weak, setWeak] = useState(false)
  const prevRef = useRef({ lost: 0, received: 0 })
  const streakRef = useRef({ bad: 0, good: 0 })

  useEffect(() => {
    if (!enabled) {
      // Sin medir (conectando o reconectando): arranca de cero al volver.
      prevRef.current = { lost: 0, received: 0 }
      streakRef.current = { bad: 0, good: 0 }
      return
    }

    let stopped = false
    const sample = async () => {
      const pc = pcRef.current
      if (!pc || stopped) return
      try {
        const report = await pc.getStats()
        let rtt = null
        let lost = 0
        let received = 0
        report.forEach((r) => {
          if (r.type === 'candidate-pair' && (r.nominated || r.selected) && typeof r.currentRoundTripTime === 'number') {
            rtt = r.currentRoundTripTime
          }
          if (r.type === 'inbound-rtp') {
            lost += r.packetsLost || 0
            received += r.packetsReceived || 0
          }
        })

        const dLost = Math.max(0, lost - prevRef.current.lost)
        const dReceived = Math.max(0, received - prevRef.current.received)
        prevRef.current = { lost, received }
        const total = dLost + dReceived
        const loss = total > 0 ? dLost / total : 0

        const bad = (rtt != null && rtt > BAD_RTT_S) || loss > BAD_LOSS
        const s = streakRef.current
        if (bad) { s.bad += 1; s.good = 0 } else { s.good += 1; s.bad = 0 }
        if (s.bad >= SAMPLES_TO_FLIP) setWeak(true)
        else if (s.good >= SAMPLES_TO_FLIP) setWeak(false)
      } catch {
        // getStats puede fallar mientras el peer se cierra: no es un dato de calidad.
      }
    }

    const id = setInterval(sample, SAMPLE_MS)
    return () => { stopped = true; clearInterval(id) }
  }, [enabled, pcRef])

  // Al dejar de medir (reconectando) el aviso viejo no tiene sentido.
  useEffect(() => { if (!enabled) setWeak(false) }, [enabled])

  return weak
}
